import { useTheme } from '@emotion/react';
import { Elements } from '@stripe/react-stripe-js';
import {
  loadStripe,
  StripeElementsOptionsClientSecret,
} from '@stripe/stripe-js';
import React from 'react';

const stripePromiseInc = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLIC_KEY_TOGGL_INC
);
const stripePromiseOu = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLIC_KEY_TOGGL_OU
);

interface StripeElementProps {
  clientSecret: string;
  site: string; // 'stripe_inc' | 'stripe_ou';
  children: React.ReactNode;
}

export function StripeElement({
  clientSecret,
  site,
  children,
}: StripeElementProps) {
  const theme = useTheme();

  const options: StripeElementsOptionsClientSecret = {
    clientSecret,
    appearance: {
      theme: 'flat',
      variables: {
        fontFamily: "'Inter', sans-serif",
        colorText: theme.typography.colorPrimary,
        colorTextSecondary: theme.typography.colorSecondary,
        colorBackground: theme.colors.white,
        colorDanger: theme.typography.colorError,
        borderRadius: '8px',
        focusBoxShadow: theme.shadow.input,
      },
      rules: {
        '.Label': {
          fontSize: '11px',
          fontWeight: '500',
          lineHeight: '1.5',
          textTransform: 'uppercase',
          marginBottom: '4px',
        },
        '.Input': {
          padding: '8px 12px',
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '1.5',
          border: `1px solid ${theme.colors.purple[40]}`,
        },
        '.Input--invalid': {
          color: theme.typography.colorPrimary,
          border: `1px solid ${theme.colors.red[100]}`,
          boxShadow: 'none',
        },
        '.Input:hover': {
          backgroundColor: theme.hover.onWhite,
        },
        '.Error': {
          marginTop: '4px',
          fontWeight: '400',
          fontSize: '12px',
        },
      },
    },
  };

  const stripePromise =
    site === 'stripe_inc' ? stripePromiseInc : stripePromiseOu;

  if (!clientSecret || !options) return null;

  return (
    <Elements options={options} stripe={stripePromise}>
      {children}
    </Elements>
  );
}
